import React, { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const TESTIMONIALS = [
  {
    quote: {
      bg: "За 4 месеца Prime Collective утроиха органичния ни трафик. Получаваме 5× повече запитвания всеки месец и плащаме същото колкото плащахме на старата ни агенция за нула резултат.",
      en: "In 4 months Prime Collective tripled our organic traffic. We get 5× more inquiries every month and pay the same as we paid the old agency for zero results.",
    },
    name: "Деница Илиева",
    role: { bg: "CEO, DocumentorBG", en: "CEO, DocumentorBG" },
    avatar: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=400&q=80",
    rating: 5,
  },
  {
    quote: {
      bg: "Прозрачни, бързи и експертни. Това не е агенция, това е продължение на нашия екип. ROI: 4.2× за 6 месеца.",
      en: "Transparent, fast and expert. They are not an agency — they're an extension of our team. ROI: 4.2× in 6 months.",
    },
    name: "Иван Костадинов",
    role: { bg: "Marketing Director, AutoPro", en: "Marketing Director, AutoPro" },
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
    rating: 5,
  },
  {
    quote: {
      bg: "Рестартирахме бранда от нулата с тях. Сайт, лого, social. Първият месец след старта — рекорд по продажби. Препоръчвам!",
      en: "We restarted our brand from scratch with them. Site, logo, social. First month after launch — sales record. Highly recommend!",
    },
    name: "Антония Маринова",
    role: { bg: "Founder, Bella Vita", en: "Founder, Bella Vita" },
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80",
    rating: 5,
  },
];

const Testimonials = () => {
  const { lang } = useLang();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const i = setInterval(() => setIdx((p) => (p + 1) % TESTIMONIALS.length), 6500);
    return () => clearInterval(i);
  }, []);

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-28 md:py-36 overflow-hidden border-y border-brand-border"
    >
      {/* gradient bg */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-purple w-[600px] h-[600px] top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 opacity-20 animate-float-slow" />
        <div className="orb orb-pink w-[500px] h-[500px] top-1/2 right-1/4 -translate-y-1/2 opacity-20 animate-float-slower" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
            {lang === "bg" ? "★ ★ ★ — ОТЗИВИ" : "★ ★ ★ — TESTIMONIALS"}
          </p>
          <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
            {lang === "bg" ? (
              <>
                Те <span className="text-white">говорят</span>,
                <br />
                ние слушаме
              </>
            ) : (
              <>
                They <span className="text-white">talk</span>,
                <br />
                we listen
              </>
            )}
          </h2>
        </div>

        {/* Featured testimonial */}
        <div className="relative max-w-4xl mx-auto">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className={`transition-opacity duration-700 ${
                i === idx ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"
              }`}
            >
              <div className="glass-strong rounded-3xl p-8 md:p-14 text-center">
                <Quote className="w-10 h-10 text-brand-pink mx-auto mb-6" strokeWidth={1.4} />
                <p className="font-heading text-xl md:text-3xl font-medium leading-snug tracking-tight mb-8 text-white">
                  "{t.quote[lang]}"
                </p>
                <div className="flex items-center justify-center gap-1 mb-6">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-brand-purpleLight text-brand-purpleLight" />
                  ))}
                </div>
                <div className="flex items-center justify-center gap-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-brand-purple"
                  />
                  <div className="text-left">
                    <p className="font-bold text-white">{t.name}</p>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 mt-1">
                      {t.role[lang]}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* dots */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Testimonial ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === idx
                    ? "w-12 bg-gradient-to-r from-brand-purple to-brand-pink"
                    : "w-6 bg-brand-border hover:bg-brand-purple/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
